import { createId } from '@paralleldrive/cuid2';
import { UAParser, IBrowser, ICPU, IEngine, IOS, IDevice } from 'ua-parser-js';
import { OmitByValue } from 'utility-types';
import isSSR from '@shared/utils/isSSR';
import { addError } from '@shared/utils/apm';
import { SendDeviceInput } from './type';

type UAInfo<T, Fields extends OmitByValue<T, Function> = OmitByValue<T, Function>> = Partial<Fields>;

interface DeviceInfo {
  deviceId: string;
  ua: string;
  browser: UAInfo<IBrowser>;
  cpu: UAInfo<ICPU>;
  device: UAInfo<IDevice>;
  os: UAInfo<IOS>;
  engine: UAInfo<IEngine>;
}

export type PersistFields = Pick<DeviceInfo, 'deviceId' | 'ua'>;

type Nullable<T> = T | null | undefined;

export type PersistStore = {
  save: (deviceInfo: PersistFields) => void;
  load: () => Promise<Nullable<PersistFields>> | Nullable<PersistFields>;
};

export interface DeviceInput {
  ua?: string;
  persistStore?: PersistStore;
  onReady?: () => void;
}

export class Device implements Omit<DeviceInfo, 'deviceId'> {
  deviceId?: string = undefined;

  ua: string;
  browser: UAInfo<IBrowser>;
  cpu: UAInfo<ICPU>;
  device: UAInfo<IDevice>;
  os: UAInfo<IOS>;
  engine: UAInfo<IEngine>;
  persistStore?: PersistStore;
  onReady?: () => void;

  _isReady: boolean = false;

  constructor(input: DeviceInput = {}) {
    this.ua = input?.ua ?? (isSSR ? '' : globalThis.navigator.userAgent);
    this.persistStore = input.persistStore;
    this.onReady = input.onReady;

    const parser = UAParser(this.ua);
    if (!isSSR) {
      parser.withFeatureCheck();
    }

    this.browser = parser.browser;
    this.cpu = parser.cpu;
    this.device = parser.device;
    this.os = parser.os;
    this.engine = parser.engine;
  }

  async init() {
    try {
      const deviceInfo = await this?.persistStore?.load();
      if (deviceInfo) {
        this.deviceId = deviceInfo.deviceId;
      } else {
        const newId = createId();
        this.deviceId = newId;
        this.persistStore?.save({
          deviceId: newId,
          ua: this.ua,
        });
      }
    } catch (e) {
      // 저장소(idb) 접근이 막힌 기기 — ready가 영영 안 되면 로그 전송이 전부 멈추므로
      // 세션 한정 deviceId로 강등하고 측정은 계속한다.
      this.deviceId = createId();
      addError(e instanceof Error ? e : new Error('device persist load failed'), {
        'error.type': 'DevicePersistLoadError',
      });
    }

    this._isReady = true;
    this.onReady?.();
  }

  toInputData(): SendDeviceInput | null {
    if (!this.deviceId) {
      return null;
    }

    return {
      id: this.deviceId,
      ua: this.ua,
      type: this.device.type,
      os: this.os.name,
      browser: this.browser.name,
    };
  }
  isReady() {
    return this._isReady;
  }
}
